import type {
  Invoice,
  DelinquencyStats,
  DelinquentStudent,
  RevenueReport,
  PlanRevenueReport,
  FinancialSummary,
  StudentFinancial,
  BillingConfig,
} from '@/types/financial'
import type {
  InvoiceApiResponse,
  DelinquencyStatsApiResponse,
  DelinquentStudentApiResponse,
  RevenueReportApiResponse,
  PlanRevenueReportApiResponse,
  FinancialSummaryApiResponse,
  StudentFinancialApiResponse,
  BillingConfigApiResponse,
} from './financial.dto'

export function mapInvoiceApiResponseToInvoice(api: InvoiceApiResponse): Invoice {
  return {
    id: api.id,
    companyId: api.companyId ?? '',
    studentId: api.student?.id ?? api.studentId ?? '',
    studentName: api.student?.name ?? api.studentName ?? '',
    planId: api.planId ?? null,
    planName: api.planName ?? null,
    calendarEventId: api.calendarEventId ?? null,
    type: api.type ?? 'one_time',
    status: api.status ?? 'pending',
    amountCents: api.amountCents ?? 0,
    dueDateAt: api.dueDateAt ?? '',
    paidAt: api.paidAt ?? null,
    paymentMethod: api.paymentMethod ?? null,
    manualPayment: api.manualPayment ?? false,
    asaasPaymentId: api.asaasPaymentId ?? null,
    invoiceUrl: api.invoiceUrl ?? null,
    nfseNumber: api.nfseNumber ?? null,
    nfseStatus: api.nfseStatus ?? null,
    description: api.description ?? '',
    createdAt: api.createdAt ?? '',
    updatedAt: api.updatedAt ?? api.createdAt ?? '',
  }
}

export function mapDelinquencyStatsApiResponseToDelinquencyStats(
  api: DelinquencyStatsApiResponse,
): DelinquencyStats {
  return {
    totalStudents: api.totalStudents ?? 0,
    totalAmountCents: api.totalAmountCents ?? 0,
    delinquencyRate: api.delinquencyRate ?? 0,
    avgDaysOverdue: api.avgDaysOverdue ?? 0,
  }
}

export function mapDelinquentStudentApiResponseToDelinquentStudent(
  api: DelinquentStudentApiResponse,
): DelinquentStudent {
  return {
    invoiceId: api.invoiceId,
    studentId: api.studentId,
    studentName: api.studentName ?? '',
    planName: api.planName ?? null,
    amountCents: api.amountCents ?? 0,
    daysOverdue: api.daysOverdue ?? 0,
    dueDateAt: api.dueDateAt ?? '',
  }
}

export function mapRevenueReportApiResponseToRevenueReport(
  api: RevenueReportApiResponse,
): RevenueReport {
  return {
    currentMonthCents: api.currentMonthCents ?? 0,
    previousMonthCents: api.previousMonthCents ?? 0,
    variationPercent: api.variationPercent ?? 0,
    monthlyData: (api.months ?? []).map((m) => ({ month: m.month, amountCents: m.totalCents })),
  }
}

export function mapPlanRevenueReportApiResponseToPlanRevenueReport(
  api: PlanRevenueReportApiResponse,
): PlanRevenueReport[] {
  return (api.plans ?? []).map((p) => ({
    planId: p.planId,
    planName: p.planName ?? '',
    studentCount: p.studentCount ?? 0,
    revenueCents: p.totalCents ?? 0,
    averageTicketCents: p.averageTicketCents ?? 0,
  }))
}

export function mapFinancialSummaryApiResponseToFinancialSummary(
  api: FinancialSummaryApiResponse,
): FinancialSummary {
  return {
    revenueCents: api.currentMonthRevenueCents ?? 0,
    revenueVariationPercent: api.revenueVariationPercent ?? 0,
    delinquencyRate: api.delinquencyRate ?? 0,
    delinquentCount: api.delinquentStudents ?? 0,
    delinquentAmountCents: api.totalOverdueCents ?? 0,
    averageTicketCents: api.averageTicketCents ?? 0,
  }
}

export function mapStudentFinancialApiResponseToStudentFinancial(
  api: StudentFinancialApiResponse,
): StudentFinancial {
  return {
    isDelinquent: api.stats?.financialStatus === 'inadimplente',
    totalPaidLast12MonthsCents: api.stats?.totalPaidCents ?? 0,
    pendingInvoicesCount: (api.invoices ?? []).filter((inv) => inv.status === 'pending').length,
    pendingAmountCents: api.stats?.totalPendingCents ?? 0,
    invoices: (api.invoices ?? []).map(mapInvoiceApiResponseToInvoice),
  }
}

export function mapBillingConfigApiResponseToBillingConfig(
  api: BillingConfigApiResponse,
): BillingConfig {
  return {
    id: api.id,
    companyId: api.companyId ?? '',
    asaasApiKeyConfigured: api.hasApiKey ?? false,
    asaasWalletId: api.asaasWalletId ?? null,
    asaasEnvironment: api.asaasEnvironment ?? 'sandbox',
    accountType: api.accountType ?? 'cnpj',
    autoInvoiceEnabled: api.autoInvoiceEnabled ?? false,
    municipalServiceId: api.municipalServiceId ?? null,
    municipalServiceCode: api.municipalServiceCode ?? null,
    issRate: api.issRate ?? null,
    municipalRegistration: api.municipalRegistration ?? null,
    defaultServiceDescription: api.defaultServiceDescription ?? null,
    dunningEnabled: api.dunningEnabled ?? false,
    dunningDaysBefore: api.dunningDaysBefore ?? null,
    dunningDaysAfter: api.dunningDaysAfter ?? null,
    maxRetries: api.maxRetries ?? null,
    delinquencyBlockEnabled: api.delinquencyBlockEnabled ?? false,
    delinquencySoftBlockDays: api.delinquencySoftBlockDays ?? 7,
    delinquencyHardBlockDays: api.delinquencyHardBlockDays ?? 30,
    createdAt: api.createdAt ?? '',
    updatedAt: api.updatedAt ?? api.createdAt ?? '',
  }
}
